export const meaning = [
  [
    `The team avoids discomfort, which limits trust, truth-telling, and problem-solving.`,
    `Discomfort is acknowledged but pushed offline, limiting shared learning and resolution.`,
    `Mixed responses to discomfort create uneven dialogue and uneven emotional safety.`,
    `Team can manage discomfort, but only sometimes or inconsistently.`,
    `The team engages in discomfort well, but may lack repair or emotional closure.`,
    `Discomfort is embraced as growth—yet overuse can fatigue or desensitize the group.`,
  ],[
    `You’re operating below the group’s discomfort capacity, which can create internal disconnection and missed contribution.`,
    `You’re ahead of the team but likely feeling stuck or unheard, which can build frustration.`,
    `Your capacity is higher than the group’s, and the mismatch may impact group cohesion.`,
    `You try to stay engaged but reach your limit earlier than others, which can leave you feeling exposed.`,
    `You match the group’s discomfort capacity, supporting consistent and shared courage.`,
    `You slightly lead the group in discomfort tolerance, helping stretch the team’s threshold.`,
  ],[
    `Important thoughts or concerns remain unspoken, weakening decision quality and team trust.`,
    `A few voices dominate while others stay silent, creating hidden tension or imbalance.`,
    `Sharing is common when it’s safe, but riskier thoughts still stay buried.`,
    `Tough topics are raised by some, but not all; safety is good but not consistent.`,
    `Team is open and honest, though small blind spots may still linger.`,
    `There’s full transparency and ease in sharing — a psychologically safe, high-performing environment.`,
  ],[
    `Conflict feels unsafe or unproductive, so critical issues remain unresolved or unexplored.`,
    `Team avoids disagreement, prioritising harmony over insight.`,
    `Debate is possible but shallow — important tensions are sidestepped.`,
    `Conflict works for minor issues, but depth and stakes still trigger avoidance.`,
    `Key issues are debated well, but some team members may still hold back.`,
    `Team challenges each other constructively, even on high-stakes issues.`,
  ],[
    `The team operates in silos, limiting collaboration, shared ownership, and early input.`,
    `Pockets of awareness exist, but lack of visibility still slows alignment.`,
    `There’s good awareness but low cross-commentary — support remains passive.`,
    `Feedback happens sometimes, but isn’t yet habitual or embedded.`,
    `Meaningful cross-input is happening, but may still rely on certain individuals.`,
    `There is shared ownership and proactive contribution across all workstreams.`,
  ],[
    `Expectations are vague or missing, making accountability feel personal or reactive.`,
    `Informal expectations exist but are rarely reinforced, leading to inconsistency.`,
    `Expectations are known but not actively used — there is a risk of drift or misalignment.`,
    `There’s occasional accountability, but it can be uneven or avoided when awkward.`,
    `Expectations shape behaviour consistently — though formality may reduce reflection.`,
    `Accountability is mutual and trusted — expectations are embedded in culture.`,
  ],[
    `Meetings are one-way, limiting engagement, ownership, and shared insight.`,
    `Conversation feels forced or staged, not spontaneous or collaborative.`,
    `Some voices dominate while others observe, leading to disengagement or imbalance.`,
    `Space is created, but uptake is limited — psychological safety may still vary.`,
    `Engagement is broad and consistent, though may still rely on facilitation.`,
    `Conversations flow freely and inclusively — ideas build and bounce around the team.`,
  ],[
    `The team feels transactional — there’s no personal connection or sense of psychological availability.`,
    `You’ve broken the ice, but relationships remain shallow and lack depth or nuance.`,
    `Connections are uneven — some are close, some are distant. Trust may vary as a result.`,
    `Most people are known, but the depth isn’t consistent — some feel more seen than others.`,
    `Strong personal understanding exists, but may not be regularly refreshed or widely distributed.`,
    `The team feels deeply connected — people feel seen, supported, and understood.`,
  ],[
    `Teamwork is rarely discussed, meaning issues build up and undermine performance silently.`,
    `Team reviews happen, but only in hindsight and general terms — too late to shift.`,
    `Team reviews are regular but lack precision — limiting learning and growth.`,
    `The team can reflect in detail, but usually not until after things are finished.`,
    `Real-time sensing is present but still vague — tweaks are made, but not always intentionally.`,
    `The team adapts fluently and specifically in real time, fine-tuning its performance as it happens.`,
  ],[
    `The team avoids working on itself — behaviours don’t change, even when issues are clear.`,
    `Team reflects, but it leads nowhere — issues are named, not addressed.`,
    `Actions happen, but they’re vague or symbolic — real shifts are rare.`,
    `Team takes action, but it’s mostly collective — individual habits remain unchanged.`,
    `Action happens across levels — but follow-through may fade without reinforcement.`,
    `The team reliably acts and reflects — it holds itself to meaningful change.`,
  ],[
    `Beliefs are unclear or unspoken, so assumptions go unchallenged and limit shared learning.`,
    `Core beliefs feel fixed, limiting growth and adaptability in a changing world.`,
    `Surface-level flexibility exists, but deeper assumptions remain protected.`,
    `There’s openness to change, but hard topics are avoided or handled softly.`,
    `Debate is active, but actual belief shifts are rare — ideas circle but don’t evolve.`,
    `The team continuously evolves its thinking — challenging and revising ideas with maturity.`,
  ],[
    `The team only aligns reactively — there’s no ongoing shared narrative, so confusion builds.`,
    `Sensemaking is handed down, not co-created — people feel disconnected from the ‘why’.`,
    `Views are shared but remain fragmented — the team lacks a cohesive picture.`,
    `Alignment forms, but it can silence minority views or feel top-down.`,
    `Debate leads to shared meaning, but can be time-consuming or tense without structure.`,
    `Sensemaking is collective, dynamic, and continuous — it supports clarity and adaptability.`
  ]
]

export const action = [
  [
    `Practice naming discomfort gently and staying in the room when tension arises.`,
    `Make space to stay with the discomfort in the moment, not delay it.`,
    `Develop group norms for staying present and constructive during tension.`,
    `Strengthen your rhythm of checking in during tough moments and seeing it through.`,
    `Balance staying with the tension and ensuring resolution and relational repair.`,
    `Build in rest, humour, and moments of ease to balance intensity.`,
  ],[
    `Build confidence by staying just a little longer in tense conversations.`,
    `Support the group to stay with hard topics, without becoming resentful or pushy.`,
    `Model constructive discomfort without pushing too hard; support others to stretch.`,
    `Grow your endurance gradually, and be transparent about your stretch points.`,
    `Keep practicing mutual support and notice when others are hitting their limits.`,
    `Keep gently leading the way—without forcing—and help others feel safe enough to follow.`,
  ],[
    `Start naming small uncertainties or questions to build psychological safety.`,
    `Encourage broader participation and reduce fear of judgement or misinterpretation.`,
    `Create space for disagreement or vulnerability when stakes feel higher.`,
    `Strengthen norms so disclosure isn’t dependent on personality or role.`,
    `Stay attentive to quieter signals and keep nurturing openness.`,
    `Keep reinforcing that candour is a core value, especially as new people join.`,
  ],[
    `Begin practicing how to disagree respectfully and recover well.`,
    `Normalize respectful dissent by creating space to disagree safely.`,
    `Stretch your comfort zone by exploring meaningful differences.`,
    `Build the capacity to stay open and grounded in high-stakes disagreement.`,
    `Support consistent inclusion during tense conversations.`,
    `Keep sharpening your conflict culture — curious, respectful, and bold.`,
  ],[
    `Start surfacing who’s working on what and why — increase visibility.`,
    `Build a shared rhythm of checking in across roles and tasks.`,
    `Encourage active curiosity and respectful cross-engagement in each other’s work.`,
    `Reinforce a culture of constructive input and co-ownership.`,
    `Make feedback and insight part of the team’s default operating rhythm.`,
    `Maintain high standards of input while ensuring everyone feels included and supported.`,
  ],[
    `Create and agree on some clear shared expectations for how you work together.`,
    `Make expectations visible and revisit them regularly to build shared ownership.`,
    `Build comfort and confidence in naming expectation gaps respectfully.`,
    `Strengthen the feedback loop and make calling things out feel safe and useful.`,
    `Keep expectations alive by linking them to values, outcomes, and real moments.`,
    `Maintain trust by checking how accountability feels and supporting each other in the process.`,
  ],[
    `Create openings for team members to contribute meaningfully.`,
    `Move from structured turn-taking to open, dynamic dialogue.`,
    `Build equity of voice by actively inviting under-heard perspectives.`,
    `Strengthen norms around active contribution and shared dialogue.`,
    `Nurture habits of spontaneous, inclusive, and co-owned conversation.`,
    `Maintain fluency by adapting to group energy and inviting edge voices.`,
  ],[
    `Begin building personal awareness and rapport, starting with low-stakes sharing.`,
    `Make space for curiosity and stories that reveal values, habits, and context.`,
    `Create opportunities for more even, shared connection across the full team.`,
    `Deepen mutual understanding through shared stories, empathy, and perspective taking.`,
    `Continue nurturing intimacy with intention and make room for changes over time.`,
    `Keep weaving shared humanity into everyday interactions — especially in moments of pressure.`,
  ],[
    `Start naming how you're working together, not just what you're doing.`,
    `Begin reflecting more often and be specific about behaviour and patterns.`,
    `Get clearer and bolder about naming what’s working and what’s not.`,
    `Bring reflection closer to the moment — even mid-task or mid-meeting.`,
    `Practice naming patterns and shifts with precision as they emerge.`,
    `Sustain this fluency by staying reflective, curious, and open to ongoing calibration.`,
  ],[
    `Start with one small thing to change and agree to revisit it.`,
    `Move from talking to action by defining clear next steps.`,
    `Focus on specific, relevant changes that matter to your real work.`,
    `Link team changes to personal practices and commitments.`,
    `Strengthen the loop from insight → action → revisit.`,
    `Maintain accountability by treating improvement as a shared habit, not a project.`,
  ],[
    `Start naming the assumptions or beliefs that guide your thinking.`,
    `Get curious about alternative perspectives — start by listening for insight, not rebuttal.`,
    `Practice engaging with challenges to your core thinking, not just your opinions.`,
    `Build courage to tackle the beliefs that shape your culture and decisions.`,
    `Go beyond debate by creating psychological permission to change your mind.`,
    `Maintain a learning mindset by treating belief revision as a strength, not a weakness.`,
  ],[
    `Start naming what’s happening, not just what’s being done.`,
    `Invite more voices into the meaning-making process.`,
    `Practice actively reconciling divergent views into shared understanding.`,
    `Include dissent and debate before settling on shared meaning.`,
    `Streamline your process for real-time collaborative sensemaking.`,
    `Keep evolving shared meaning through active listening and real-time reflection.`,
  ]
]

export const practiceTips = [
  [
    `Try a "what are we avoiding?" check-in`,
    `Use ‘name it to tame it’ language`,
    `Invite one hard topic to explore`,
    `"Look at us go" - commend the team for working in the discomfort`,
    `Ask "Is this worth staying with now?"`,
    `Set a timebox for tough discussions`,
    `Pause and invite reflection instead of deferral`,
    `Reflect out loud about topics you have recently handled that previously would have been to difficult for this group. Notice the group's progress`,
    `Agree on a group signal for disengagement`,
    `Debrief after hard moments`,
    `Ask: "What helped you stay with that?"`,
    `Do a "how are we sitting with this?" pause`,
    `Reflect on one recent tricky convo`,
    `Celebrate a team win for leaning in`,
    `Use a 1–10 tension scale`,
    `Add lightness or humour after tension`,
    `Schedule breathing space post-debate`
  ],
  [
    `Name when you feel like pulling away`,
    `Ask a teammate for backup in tense moments`,
    `Reflect privately after discomfort passes`,
    `Frame discomfort as useful before speaking up`,
    `Share your intent when you raise a tough topic `,
    `Invite others to lean in with you`,
    `Agree and use a hand signal or "safe word" to signal that you are at your upper levels and limits for discomfort`,
    `Ask: "Is it ok if we stay with this a bit longer?"`,
    `Pair with someone who finds it harder`,
    `Debrief with someone after`,
    `Take breaks without withdrawing fully`,
    `Reflect on what helps you stay engaged`,
    `Ask: "Is anyone holding back right now?"`,
    `Encourage moments of regrouping`,
    `Acknowledge when it feels hard but worthwhile`,
    `Ask: "Should we stick with this a bit longer?"`,
    `Validate others’ effort to stay engaged`
  ],
  [
    `Invite a "what’s on your mind but unsaid?" round`,
    `Leaders share a moment of doubt or learning`,
    `Normalize saying "this might be nothing but..."`,
    `Celebrate vulnerability when shown`,
    `Ask: "What feels hard to say right now?"`,
    `Reflect on a time you stayed silent and why`,
    `Say: "I have a knot in my stomach as I say this, but I will say it"...`,
    `Set the tone with a risky question`,
    `Rotate facilitation to shift dynamics`,
    `Thank individuals who disclose things to the group`,
    `Run a "what’s just under the surface?" round`,
    `Ask: "What haven’t we heard yet?"`,
    `Invite reflections after big topics land`,
    `Celebrate honesty when it’s shown`,
    `Make space for "anything else?" moments`,
    `Invite new members to reflect on team openness`
  ],
  [
    `Debrief a past disagreement: what worked, what didn’t`,
    `Set ground rules for handling conflict`,
    `Say: "thanks for staying in this chat"`,
    `Say: "This is important and not easy"`,
    `Ask: "Is anyone seeing this differently?"`,
    `Invite devil’s advocate views`,
    `Reflect on when polite silence caused confusion`,
    `Ask: "What’s at stake if we disagree here?"`,
    `Reflect on a tough issue that’s gone unspoken`,
    `Have a practice disagreement on a trivial topic like "hamburger beats dumplings". Notice the group's skill at that debate. Take that skill to the topic that really does matter`,
    `Pause to name when a topic feels important`,
    `Slow down during emotional moments`,
    `Practice "I see it differently because..."`,
    `Ask: "Has everyone had a say on this?"`,
    `Reflect on team dynamics in conflict`,
    `Check in privately with quieter voices`,
    `Celebrate when healthy debate happens`,
    `Review a tough decision and how it was made`,
    `Ask: "How did we grow from that?"`
  ],
  [
    `Do a quick "What’s on your plate this week?" round`,
    `Share one challenge each person is working through`,
    `Map roles and interdependencies on a shared board`,
    `Run a "who’s doing what" sprint review`,
    `Ask: "Is anyone stuck where we could help?"`,
    `Include others when sharing early ideas - work in draft`,
    `Ask: "Can I offer a thought on that?"`,
    `Reflect on: "When did we help each other last week?"`,
    `Invite feedback before finalising work`,
    `Nominate a peer reviewer for key pieces of work`,
    `Celebrate examples of helpful challenge`,
    `Rotate work-in-progress show & tells`,
    `Ask: "What do you see that I don’t?" about your own work`,
    `Track how team input influenced outcomes`,
    `Review: "Where have we made each other better this month?"`,
    `Invite all voices into retrospectives`
  ],
  [
    `Ask: "What do we expect from each other here?"`,
    `Co-create a list of team norms`,
    `Reflect on what’s felt unclear recently`,
    `Write your expectations down`,
    `Review how often expectations are mentioned in meetings, Check: "Are we living this?"`,
    `"Will we look back on this moment in a year's time and be proud of how we have worked?"`,
    `Reflect on what’s working and what’s slipping`,
    `Practice holding one another to a norm`,
    `Ask: "What expectation felt most real this week?"`,
    `Reflect on how it feels to speak up`,
    `Reconnect norms to your purpose or strategy`,
    `Use real scenarios to review expectations in action`,
    `Reflect on how norms have evolved`,
    `Ask: "What makes accountability feel fair here?"`,
    `Share appreciation when someone lives a norm well`,
    `Refresh expectations each quarter`
  ],
  [
    `Ask: "Who haven’t we heard from yet?"`,
    `Invite reactions to each others' contributions before moving on`,
    `Use "popcorn" style speaking turns`,
    `Encourage building on each other’s ideas`,
    `Shift facilitation across meetings`,
    `Take a blank sheet of paper and draw a pie chart of who has been speaking in this meeting. Show each other the drawings and discuss`,
    `Set a norm of "step up, step back"`,
    `Ask quieter members what helps them speak up`,
    `Prime people before meetings with key questions`,
    `Reflect: "What helped you contribute today?"`,
    `Create roles (e.g. challenger, supporter)`,
    `Start with a peer-to-peer warmup round`,
    `Experiment with peer-led segments`,
    `Reflect on flow: "Did it feel shared today?"`,
    `Ask: "Who sparked something new today?"`,
    `Encourage different people to close the meeting`,
  ],
  [
    `Ask: "What’s something that made you smile this week?"`,
    `Write down proudest professional accomplishment in the last 3-6 months. Share`,
    `Write down proudest personal accomplishment in the last 3-6 months. Share`,
    `Share one thing outside of work you enjoy`,
    `Try a "how I work best" round`,
    `Ask: "What’s one thing people misunderstand about you?"`,
    `Share a challenge and how you’re handling it`,
    `Run a personal map activity`,
    `Reflect: "Who do I know less well, and why?"`,
    `Run a "life timeline" or work journey story session`,
    `Invite people to share what shaped them`,
    `Ask: "What’s important for us to know about you?"`,
    `Ask: "What’s something that’s shifted for you lately?"`,
    `Share one recent joy and one stressor`,
    `Ask: What is something you used to believe as a child that you no longer believe?`,
    `Celebrate team moments that show care`,
    `Keep making space for personal storytelling`
  ],
  [
    `Ask: "How did we show up as a team today?"`,
    `Reflect on group energy at the end`,
    `Try a ‘teamwork traffic light’ check-in`,
    `Pay attention at three positions: content, colleagues, self`,
    `Capture one specific dynamic to improve`,
    `Introduce a midpoint team reflection`,
    `Pause and ask: "How are we going right now?"`,
    `Capture insights immediately while fresh`,
    `Ask: "What shift just happened here?"`,
    `Label a moment when dynamics changed`,
    `Keep shared notes on real-time team flow`,
    `Ask: "What helped us pivot just now?"`
  ],
  [
    `Ask: "What’s one thing we could try differently?"`,
    `Pick one behaviour to test this week`,
    `End meetings with: "What will we do with this?"`,
    `Ask: "What struck you about our dynamic today?"`,
    `Ask: "What do those things that struck us mean?"`,
    `Ask: "So what should we do about what we noticed?"`,
    `Share progress or challenges next meeting`,
    `Reflect: "What did I personally do differently?"`,
    `Set timeframes for each team and personal action`,
    `Ask: "Have we done what we said we would?"`,
    `Celebrate traction, not perfection`,
    `Share wins from recent changes`,
    `Ask: "What’s next for us to level up?"`,
    `Celebrate a moment of live adjustment`
  ],
  [
    `Ask: "What might we be assuming here?"`,
    `Share one belief you hold and why`,
    `Reflect on what’s shaped your view on a key topic`,
    `Try a "change my mind" question round`,
    `Pair up and argue the opposite view to your own`,
    `Reflect on a time you changed your mind at work`,
    `Ask: "What belief might be limiting us here?"`,
    `Reflect on how your worldview affects decision-making`,
    `Invite feedback on blind spots`,
    `Discuss: "What’s a belief we’ve never questioned?"`,
    `Use roleplay or stories to test assumptions`,
    `Ask: "What would it take to shift this?"`,
    `Say: "Here’s something I’ve changed my mind on"`,
    `Appreciate when someone models growth`,
    `Share new insight that reshaped your thinking`,
    `Celebrate examples of changed minds`,
    `Ask: "What belief have we outgrown?"`,
    `Reflect: "How has our thinking evolved lately?"`,
  ],
  [
    `Distinguish between Agreement, Consensus and Alignment.  What are we aiming for here?`,
    `Ask: "What’s our sense of what’s going on right now?"`,
    `Use a timeline to map different perspectives`,
    `Reflect on how alignment is (or isn’t) happening`,
    `Ask: "How do you see this situation?"`,
    `Share your own evolving take`,
    `Use prompts like "what’s your read on this?"`,
    `Use a whiteboard to cluster perspectives`,
    `Ask: "Where are we aligned and where not?"`,
    `Create a joint statement of understanding`,
    `Ask: "What’s the view we’re not hearing?"`,
    `Role-play different stakeholder perspectives`,
    `Reflect on groupthink tendencies`,
    `Use a "Scan–Share–Synthesize" format`,
    `Nominate a sensemaking facilitator`,
    `Capture a 3-sentence summary together at the end`,
    `Ask: "What’s shifted in our understanding this week?"`,
    `Review shared language and metaphors`,
    `Hold a regular sensemaking sprint`,
  ]
]

export const recommendedGame = [
  {
    game: 'grimsby',
    why: `This game immerses teams in high-pressure scenarios, challenging them to balance task execution with interpersonal dynamics. It fosters resilience and the ability to stay engaged during discomfort, mirroring the sub-practice's focus.`
  }, {
    game: 'grimsby',
    why: `Participants navigate personal and team challenges, encouraging self-awareness and growth in managing discomfort within team settings.`
  }, {
    game: 'critters',
    why: `This game emphasizes building trust and psychological safety, encouraging team members to share openly and reduce social risk.`
  }, {
    game: 'dragons',
    why: `Teams face scenarios requiring them to navigate conflicts between personal and group goals, promoting constructive disagreement and collaboration.`
  }, {
    game: 'critters',
    why: `The game challenges teams to manage resources collectively, fostering a sense of shared responsibility and breaking down silos.`
  }, {
    game: 'grimsby',
    why: `Success in the game hinges on clear communication and mutual understanding of roles and expectations, aligning with this sub-practice.`
  }, {
    game: 'critters',
    why: `The game requires inclusive participation and values diverse input, encouraging equitable contribution from all team members.`
  }, {
    game: 'critters',
    why: `By simulating a community of critters, the game fosters deeper interpersonal connections and understanding among team members.`
  }, {
    game: 'first-responder',
    why: `Teams must adapt to evolving challenges in real-time, enhancing their ability to sense and respond to changes effectively.`
  }, {
    game: 'first-responder',
    why: `The game emphasizes translating insights into immediate actions, reinforcing the importance of timely execution.`
  }, {
    game: 'grimsby',
    why: `Participants must reassess their strategies based on feedback and outcomes, promoting continuous learning and adaptation.`
  }, {
    game: 'dragons',
    why: `Success relies on collective interpretation of challenges and collaborative strategy development, aligning with co-creation of understanding.`
  }
]